// =============================================================================
// Optim TURPE v2 — adaptateur payload (DEV-715/735).
// Le doc `AnalyseV2` réel niche tout sous `outputs.{actuelle,meilleure,diagnostic}`
// (forme pricer) + un bloc `restitution` aplati construit côté courtage. Le front
// consomme un view-model plat (`OptimTurpeV2Etude`). Cet adaptateur fait le pont :
// il lit `outputs.restitution` (couts/economie/segment/pointes/explain) + les
// structures tarifaires + le diagnostic, et reconstruit le view-model.
//
// Idempotent : un objet déjà au format view-model (pas de `outputs`, ex. fixtures
// mock) est renvoyé tel quel — mock et payload réel partagent le même chemin.
// =============================================================================

import type {
    OptimTurpeV2BorneRaccordement,
    OptimTurpeV2Calcul,
    OptimTurpeV2Conduite,
    OptimTurpeV2Controles,
    OptimTurpeV2CostIndicators,
    OptimTurpeV2Diagnostic,
    OptimTurpeV2Economie,
    OptimTurpeV2EntreeRegistre,
    OptimTurpeV2EtatMotif,
    OptimTurpeV2EtatServi,
    OptimTurpeV2Etude,
    OptimTurpeV2Explain,
    OptimTurpeV2Periode,
    OptimTurpeV2PlageKey,
    OptimTurpeV2PlancherModulation,
    OptimTurpeV2QualiteDonnees,
    OptimTurpeV2Reserve,
    OptimTurpeV2Scenario,
    OptimTurpeV2Segment,
    OptimTurpeV2StructureTarifaire,
} from "@modules/courtage/types/optim-turpe-v2"

/** Un scénario tel que stocké dans `outputs` (structure seule ; les coûts vivent dans `restitution`). */
type RawScenario = { structure_tarifaire?: OptimTurpeV2StructureTarifaire }

/** Le bloc `outputs` du doc `AnalyseV2` réel. */
type RawOutputs = {
    source?: { idsource?: string }
    compteur?: { raccordement?: { puissance_raccordement_soutirage?: number } }
    actuelle?: RawScenario
    meilleure?: RawScenario
    diagnostic?: OptimTurpeV2Diagnostic
    restitution?: {
        segment?: OptimTurpeV2Segment
        unite_puissance?: "kVA" | "kW"
        couts?: {
            actuelle?: OptimTurpeV2CostIndicators
            meilleure?: OptimTurpeV2CostIndicators
        }
        economie?: OptimTurpeV2Economie
        pointes_mesurees_kw?: Partial<Record<OptimTurpeV2PlageKey, number>>
        // courtage#254 (DEV-2185) : la clé neutre et son unité.
        pointes_mesurees?: OptimTurpeV2Etude["pointes_mesurees"]
        pointes_mesurees_unite?: OptimTurpeV2Etude["pointes_mesurees_unite"]
        explain?: OptimTurpeV2Explain
        reserves?: OptimTurpeV2Reserve[]
        qualite_donnees?: OptimTurpeV2QualiteDonnees
        // DEV-2422 (courtage v190) : le chiffrage dédié du C5 sous le pic.
        chiffrage_puissance_adaptee?: OptimTurpeV2Etude["chiffrage_puissance_adaptee"]
        plancher_modulation?: OptimTurpeV2PlancherModulation
        comptage_resolu?: OptimTurpeV2Etude["comptage_resolu"]
        // =====================================================================
        // Champs SERVIS par le moteur V2 relu (DEV-1907/1909/1910/1911/1912),
        // tous sous `outputs.restitution` côté serveur. Ils sont facultatifs :
        // une étude calculée avant ces lots n'en porte aucun, et l'adaptateur
        // les laisse alors `undefined` — ce que les lecteurs traduisent en
        // absence DITE, jamais en 0 ni en état inventé.
        // =====================================================================
        etat?: OptimTurpeV2EtatServi | null
        etat_motif?: OptimTurpeV2EtatMotif | string | null
        etat_cause?: string | null
        etat_seuil_eur?: number | null
        periode?: OptimTurpeV2Periode | null
        scenario?: string | null
        bornes?: { raccordement?: OptimTurpeV2BorneRaccordement | null } | null
        pmax_mesuree?: OptimTurpeV2Etude["pmax_mesuree"]
        controles?: OptimTurpeV2Controles | null
        entrees?: OptimTurpeV2EntreeRegistre[] | null
        conduite?: OptimTurpeV2Conduite | null
        /** Branchement retenu par le moteur en C5 (DEV-1911) : dit si le point est triphasé. */
        branchement?: OptimTurpeV2Etude["branchement"]
        // Champs servis par courtage#249 (revue du 25/09/2026).
        injection?: OptimTurpeV2Etude["injection"]
        plage_motrice?: OptimTurpeV2Etude["plage_motrice"]
        cmdps_applicable?: OptimTurpeV2Etude["cmdps_applicable"]
        demande?: OptimTurpeV2Etude["demande"]
        marche_a_suivre?: OptimTurpeV2Etude["marche_a_suivre"]
        // Champs servis par courtage#254 (DEV-2183).
        etat_nette_comparee_eur?: OptimTurpeV2Etude["etat_nette_comparee_eur"]
        etat_inegalite?: OptimTurpeV2Etude["etat_inegalite"]
        recommandation?: OptimTurpeV2Etude["recommandation"]
    }
    /** Reproductibilité du chiffre (§6) — frère de `restitution`, pas son enfant. */
    calcul?: OptimTurpeV2Calcul | null
    /** L'arbitrage de la paire (courtage#251, DEV-2167), frère de `restitution` lui aussi. */
    arbitrage?: OptimTurpeV2Etude["arbitrage"]
    /** L'état de la narration (courtage#274, DEV-2311), frère de `restitution`. */
    narration?: OptimTurpeV2Etude["narration"]
}

/** Étude réelle (renvoyée par la façade) : champs tenant top-level + bloc `outputs`. */
export type OptimTurpeV2RawEtude = Partial<OptimTurpeV2Etude> & {
    // `null` explicitement : c'est ce que le moteur persiste sur une étude
    // pré-allouée en `PENDING`, et le taire ferait mentir le type (DEV-1529).
    outputs?: RawOutputs | null
}

/**
 * Champs que l'adaptateur PRODUIT et que le document du moteur ne porte jamais à
 * sa racine : ils vivent tous sous `outputs.restitution` côté serveur. Leur
 * présence en surface signe donc un objet DÉJÀ au format view-model (fixture
 * mock, ou étude déjà adaptée), le seul cas où le passthrough est légitime.
 */
const MARQUEURS_VIEW_MODEL = [
    "compteur",
    "economie",
    "actuelle",
    "meilleure",
    "explain",
    "pointes_mesurees_kw",
    "pointes_mesurees",
] as const

/**
 * L'objet est-il déjà un view-model ?
 *
 * ⚠️ DEV-1529, le fait à ne pas répéter : la condition d'origine était « pas de
 * bloc `outputs` → passthrough ». Elle a été écrite quand le seul objet sans
 * `outputs` était une fixture mock. Depuis que le moteur pré-alloue l'étude en
 * `PENDING` et rend 202, un DOCUMENT SERVEUR arrive lui aussi sans `outputs` :
 * il repartait tel quel, restait truthy, et la page l'affichait comme un
 * résultat (`v-if="getCurrentEtude"`) alors qu'il n'a ni économie ni coûts.
 *
 * Le passthrough exige donc désormais une preuve POSITIVE : au moins un champ
 * que seul le view-model porte. Un document brut sans `outputs` passe par le
 * chemin d'adaptation, qui rend une étude honnête (son `status`, son `error`,
 * ses `inputs`, et rien d'autre) au lieu d'un faux résultat.
 */
function estDejaViewModel(
    raw: OptimTurpeV2RawEtude | OptimTurpeV2Etude
): boolean {
    return MARQUEURS_VIEW_MODEL.some(
        (champ) => (raw as Record<string, unknown>)[champ] !== undefined
    )
}

/** Le code de la réserve que la page ne montre plus (R10, 08/10/2026). */
export const RESERVE_UNITE_PRESUMEE = "unite-presumee"

/** Les réserves servies, sans `unite-presumee` (R10). `undefined` reste `undefined`. */
export function sansReserveDUnitePresumee<T extends { code?: string | null }>(
    reserves: T[] | null | undefined
): T[] | undefined {
    if (!Array.isArray(reserves)) return reserves ?? undefined
    return reserves.filter((r) => r?.code !== RESERVE_UNITE_PRESUMEE)
}

// =============================================================================
// R14 (revue du 08/10/2026) : « Pourquoi on a une puissance souscrite à
// vérifier ? Soit ça vient d'Enedis, soit l'utilisateur a modifié un truc. »
//
// Ce que le client doit vivre : la puissance souscrite actuelle est celle lue
// chez Enedis ou celle qu'il a corrigée ; l'écran ne lui demande plus de la
// vérifier. Quand elle est très au-dessus des appels mesurés, c'est justement
// ce que l'économie mesure. La réserve `denivele-actuel-peu-vraisemblable` ne
// produit donc plus rien sur les écrans client : ni la pastille « puissances
// souscrites actuelles à vérifier », ni la ligne « Dépassements … Vérifiez-la »
// du temps 1, ni l'étape « Vérifier sur votre contrat … » de la marche à
// suivre, ni la condition « vérifier d'abord la puissance souscrite actuelle »
// de la phrase de démarche. Le fait reste servi dans `controles.denivele_actuel`
// (registre, dossier de l'équipe).
//
// Le moteur peut encore servir l'ancien texte (prod entre deux déploiements,
// études d'avant) : la décision se prend sur le CODE de la réserve, jamais sur
// son libellé. Seule la condition de la phrase de démarche, qui n'a pas de code
// propre, se retire sur ses formes servies, et seulement quand la réserve est là.
// =============================================================================

/** Le code de la réserve que les écrans client ne disent plus (R14). */
export const RESERVE_PUISSANCE_ACTUELLE_A_VERIFIER =
    "denivele-actuel-peu-vraisemblable"

/** Le code de l'étape que le moteur pose en tête de la marche pour cette réserve. */
export const ETAPE_VERIFIER_LA_PUISSANCE_ACTUELLE =
    "verifier-la-puissance-actuelle"

const NOMBRES_DE_CONDITIONS: Record<string, number> = {
    deux: 1,
    trois: 2,
    quatre: 3,
    cinq: 4,
    six: 5,
}
const NOMS_DES_NOMBRES: Record<number, string> = {
    2: "deux",
    3: "trois",
    4: "quatre",
    5: "cinq",
}

/**
 * La condition « vérifier la puissance souscrite actuelle » retirée de la phrase
 * de démarche servie (`conditions_de_la_demarche.phrase_de_la_demarche`) :
 * seule, elle disparaît ; énumérée en tête (elle l'est toujours), la liste perd
 * un rang.
 */
export function sansConditionDeLaPuissanceActuelle(texte: string): string {
    return texte
        .replace(
            /, à une condition : vérifier d'abord la puissance souscrite actuelle/g,
            ""
        )
        .replace(
            /à (deux|trois|quatre|cinq|six) conditions : vérifier la puissance souscrite actuelle, (et )?/g,
            (_, nombre: string) => {
                const restantes = NOMBRES_DE_CONDITIONS[nombre]
                return restantes === 1
                    ? "à une condition : "
                    : `à ${NOMS_DES_NOMBRES[restantes]} conditions : `
            }
        )
}

/**
 * L'étude sans ce que la réserve `denivele-actuel-peu-vraisemblable` faisait
 * dire aux écrans client (R14). Rendue telle quelle (même référence) quand la
 * réserve n'est pas servie.
 */
export function sansVerificationDeLaPuissanceActuelle(
    etude: OptimTurpeV2Etude
): OptimTurpeV2Etude {
    const reserves = etude.reserves
    if (
        !Array.isArray(reserves) ||
        !reserves.some((r) => r?.code === RESERVE_PUISSANCE_ACTUELLE_A_VERIFIER)
    ) {
        return etude
    }
    const explain = etude.explain
    return {
        ...etude,
        reserves: reserves.filter(
            (r) => r?.code !== RESERVE_PUISSANCE_ACTUELLE_A_VERIFIER
        ),
        marche_a_suivre: Array.isArray(etude.marche_a_suivre)
            ? etude.marche_a_suivre.filter(
                  (e) => e?.code !== ETAPE_VERIFIER_LA_PUISSANCE_ACTUELLE
              )
            : etude.marche_a_suivre,
        explain: explain
            ? {
                  ...explain,
                  ...(typeof explain.conclusion === "string"
                      ? {
                            conclusion: sansConditionDeLaPuissanceActuelle(
                                explain.conclusion
                            ),
                        }
                      : {}),
                  ...(typeof explain.pourquoi === "string"
                      ? {
                            pourquoi: sansConditionDeLaPuissanceActuelle(
                                explain.pourquoi
                            ),
                        }
                      : {}),
              }
            : explain,
    }
}

/**
 * Adapte une Étude brute (`{…, outputs:{actuelle,meilleure,diagnostic,restitution}, warnings}`)
 * vers le view-model `OptimTurpeV2Etude`. Renvoie tel quel si déjà au format view-model.
 * Dans les deux cas, la réserve R14 est écartée (`sansVerificationDeLaPuissanceActuelle`).
 */
export function adaptOptimTurpeV2Etude(
    raw: OptimTurpeV2RawEtude | OptimTurpeV2Etude | null | undefined
): OptimTurpeV2Etude | null {
    const etude = adapterSansR14(raw)
    return etude ? sansVerificationDeLaPuissanceActuelle(etude) : etude
}

function adapterSansR14(
    raw: OptimTurpeV2RawEtude | OptimTurpeV2Etude | null | undefined
): OptimTurpeV2Etude | null {
    if (!raw || typeof raw !== "object") {
        return null
    }
    // Déjà view-model (mock / déjà adapté) → passthrough. Un document SERVEUR
    // sans `outputs` (étude `PENDING` mise en file, `FAILURE` écrit avant tout
    // calcul) ne remplit PAS cette condition : il passe par l'adaptation
    // ci-dessous et ne peut plus être servi comme un résultat (DEV-1529).
    if ((!("outputs" in raw) || !raw.outputs) && estDejaViewModel(raw)) {
        return raw as OptimTurpeV2Etude
    }

    const o: RawOutputs = ("outputs" in raw ? raw.outputs : undefined) ?? {}
    const r = o.restitution ?? {}

    const scenario = (
        raw_scenario?: RawScenario,
        couts?: OptimTurpeV2CostIndicators
    ): OptimTurpeV2Scenario => ({
        structure_tarifaire: raw_scenario?.structure_tarifaire,
        couts,
    })

    return {
        _id: raw._id,
        _id_company: raw._id_company,
        _id_user: raw._id_user,
        _id_building: raw._id_building,
        type: raw.type,
        status: raw.status,
        // Cause nommée d'un échec (DEV-1583) : la relecture d'après panne en a besoin
        // pour distinguer un FAILURE explicable d'un FAILURE muet.
        error: raw.error,
        created_at: raw.created_at,
        updated_at: raw.updated_at,
        inputs: raw.inputs,
        source: o.source,
        compteur: {
            segment: r.segment,
            raccordement: o.compteur?.raccordement,
        },
        actuelle: scenario(o.actuelle, r.couts?.actuelle),
        meilleure: scenario(o.meilleure, r.couts?.meilleure),
        diagnostic: o.diagnostic,
        pointes_mesurees_kw: r.pointes_mesurees_kw,
        pointes_mesurees: r.pointes_mesurees,
        pointes_mesurees_unite: r.pointes_mesurees_unite,
        warnings: raw.warnings,
        // R10 (revue du 08/10/2026) : l'unité du raccordement est celle du
        // segment, elle ne se confirme plus. Un moteur qui sert encore la
        // réserve `unite-presumee` (la prod entre deux déploiements, ou une
        // étude d'avant) ne la fait plus peindre « à confirmer » : elle est
        // écartée ici, une fois pour toutes les lectures de la page.
        reserves: sansReserveDUnitePresumee(r.reserves),
        qualite_donnees: r.qualite_donnees,
        comptage_resolu: r.comptage_resolu,
        plancher_modulation: r.plancher_modulation,
        economie: r.economie,
        explain: r.explain,
        // =====================================================================
        // Les champs neufs du moteur V2 relu, propagés TELS QUELS (DEV-1913,
        // revue C1). Sans ce relais, le document réel — qui niche tout sous
        // `outputs.restitution` — arrivait à la page dépouillé de l'état servi,
        // de la période pricée, des bornes, des contrôles et du registre : la
        // page prenait alors systématiquement sa branche de repli, même après
        // merge des six PRs du moteur.
        //
        // Aucune valeur n'est fabriquée ici : un champ que le moteur ne sert pas
        // reste `undefined`, et ce sont les lecteurs (`lireChiffre`,
        // `lireBornes`, `lireRegistre`) qui disent l'absence.
        // =====================================================================
        etat: r.etat,
        etat_motif: r.etat_motif,
        etat_cause: r.etat_cause,
        etat_seuil_eur: r.etat_seuil_eur,
        etat_nette_comparee_eur: r.etat_nette_comparee_eur,
        etat_inegalite: r.etat_inegalite,
        recommandation: r.recommandation,
        periode: r.periode,
        scenario: r.scenario,
        bornes: r.bornes,
        pmax_mesuree: r.pmax_mesuree,
        controles: r.controles,
        entrees: r.entrees,
        conduite: r.conduite,
        branchement: r.branchement,
        injection: r.injection,
        plage_motrice: r.plage_motrice,
        cmdps_applicable: r.cmdps_applicable,
        demande: r.demande,
        // DEV-2422 : relayé tel quel ; absent, il reste `undefined`.
        chiffrage_puissance_adaptee: r.chiffrage_puissance_adaptee,
        marche_a_suivre: r.marche_a_suivre,
        // `calcul` est frère de `restitution` sous `outputs` (§6) et `cycle` vit
        // à la RACINE du document (§7) : ni l'un ni l'autre ne se lit sur `r`.
        calcul: o.calcul,
        // `arbitrage` est posé à côté de `restitution` (courtage#251) pour ne
        // pas changer l'empreinte de la narration mise en cache.
        arbitrage: o.arbitrage,
        cycle: raw.cycle,
        // La trace de mise en file vit à la racine : elle distingue une étude
        // du moteur actuel d'une étude de l'ancien moteur (DEV-2311, F1).
        reprise: raw.reprise,
        // L'état de la narration est frère de `restitution` sous `outputs`
        // (courtage#274) : la page le lit pour ne pas narrer deux fois.
        narration: o.narration,
        // Lot 4g (courtage#313, en relecture) : le scénario retenu et le canal
        // vivent à la racine de l'étude ; absents tant que le lot n'est pas
        // servi, ils restent `undefined` (aucune valeur fabriquée).
        scenario_retenu: raw.scenario_retenu,
        canal: raw.canal,
    }
}
